import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Users,
  Crown,
  Headphones,
  Code,
  Megaphone,
  DollarSign,
  MessageSquare,
  Ticket as TicketIcon,
  ChevronRight,
  Clock,
} from 'lucide-react';
import { apiClient, type TeamUserKpi } from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { cn, formatBRL, formatDate } from '../lib/utils.js';

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

function previousMonth(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 7);
}

function nextMonth(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + 1);
  return d.toISOString().slice(0, 7);
}

const roleConfig: Record<
  string,
  { label: string; color: string; icon: typeof Crown }
> = {
  founder: {
    label: 'Sócio',
    color: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
    icon: Crown,
  },
  support_l1: {
    label: 'Suporte N1',
    color: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    icon: Headphones,
  },
  support_l2: {
    label: 'Suporte N2',
    color: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
    icon: Headphones,
  },
  dev: {
    label: 'Dev',
    color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    icon: Code,
  },
  growth: {
    label: 'Growth',
    color: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    icon: Megaphone,
  },
};

function regimeColor(regime?: string): string {
  if (regime === 'PJ') return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
  if (regime === 'CLT') return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
  if (regime === 'freelancer') return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  if (regime === 'PF') return 'bg-purple-500/15 text-purple-300 border-purple-500/30';
  return 'bg-slate-500/15 text-slate-300 border-slate-500/30';
}

function UserCard({ user }: { user: TeamUserKpi }): JSX.Element {
  const role = roleConfig[user.role] ?? {
    label: user.role,
    color: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    icon: Users,
  };
  const Icon = role.icon;
  const initials = user.fullName
    .split(' ')
    .filter((p) => p.length > 0)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
  const earned = Number(user.earnedCents);
  const pending = Number(user.pendingCents);
  const paid = Number(user.paidCents);
  const resolutionRate =
    user.ticketsTotal > 0 ? (user.ticketsResolved / user.ticketsTotal) * 100 : 0;

  return (
    <Link
      to={`/equipe/${user.id}`}
      className="block group"
    >
      <Card className="hover:bg-muted/20 transition-colors">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold">
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{user.fullName}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {user.email}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className={cn('text-[10px]', role.color)}>
              <Icon className="h-3 w-3 mr-1" />
              {role.label}
            </Badge>
            {user.regime && (
              <Badge variant="outline" className={cn('text-[10px]', regimeColor(user.regime))}>
                {user.regime}
              </Badge>
            )}
            {user.hireDate && (
              <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                <Clock className="h-2.5 w-2.5" /> desde {formatDate(user.hireDate)}
              </span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border/40">
            <div>
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase">
                <DollarSign className="h-3 w-3" /> Earned
              </div>
              <div className="text-sm font-bold tabular-nums">{formatBRL(earned)}</div>
              <div className="text-[10px] text-muted-foreground">
                {user.commissionsCount} comissões
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase">
                <MessageSquare className="h-3 w-3" /> Msgs
              </div>
              <div className="text-sm font-bold tabular-nums">{user.messagesSent}</div>
              <div className="text-[10px] text-muted-foreground">enviadas</div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase">
                <TicketIcon className="h-3 w-3" /> Tickets
              </div>
              <div className="text-sm font-bold tabular-nums">{user.ticketsTotal}</div>
              <div className="text-[10px] text-muted-foreground">
                {user.ticketsResolved} resolvidos · {resolutionRate.toFixed(0)}%
              </div>
            </div>
          </div>

          {(pending > 0 || paid > 0) && (
            <div className="pt-2 border-t border-border/40 text-[10px] text-muted-foreground flex items-center justify-between">
              <span className="text-amber-400">A pagar: {formatBRL(pending)}</span>
              <span className="text-emerald-400">Pago: {formatBRL(paid)}</span>
            </div>
          )}
          {user.avgCsat && (
            <div className="text-[10px] text-muted-foreground">
              CSAT médio: <span className="text-foreground">{Number(user.avgCsat).toFixed(1)}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export function Equipe(): JSX.Element {
  const [month, setMonth] = useState<string>(currentMonth());
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['team-overview', month],
    queryFn: () => apiClient.teamOverview(month),
  });

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Carregando equipe...</div>;
  }

  if (isError || !data) {
    return <div className="p-8 text-rose-400">Erro: {String(error)}</div>;
  }

  const { users, totals } = data;

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            Equipe & Comissões
          </h1>
          <p className="text-sm text-muted-foreground">
            People analytics · Comissões · Performance individual
          </p>
        </div>

        <div className="flex items-center gap-2 bg-muted/40 rounded-md px-1 py-1 border border-border">
          <button
            onClick={() => setMonth(previousMonth(month))}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
          >
            ←
          </button>
          <div className="px-3 py-1 text-sm font-medium tabular-nums">{month}</div>
          <button
            onClick={() => setMonth(nextMonth(month))}
            disabled={month >= currentMonth()}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30"
          >
            →
          </button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Time ativo</div>
            <div className="text-2xl font-bold tabular-nums">{totals.users}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Comissões geradas</div>
            <div className="text-2xl font-bold tabular-nums">{totals.totalCommissions}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Payout total</div>
            <div className="text-2xl font-bold tabular-nums">
              {formatBRL(totals.totalPayoutCents)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">A pagar</div>
            <div className="text-2xl font-bold tabular-nums text-amber-400">
              {formatBRL(totals.pendingPayoutCents)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User cards */}
      <div className="grid lg:grid-cols-3 gap-4">
        {users.map((u) => (
          <UserCard key={u.id} user={u} />
        ))}
      </div>

      <div className="text-xs text-muted-foreground border-t border-border pt-4">
        <strong>Modelo de comissão (Shadow Ledger):</strong> Sócios recebem 50% do lucro líquido
        cada (profit share); Patricia recebe 2% sobre faturamento bruto (taxa freelancer). Em produção,
        regras configuráveis em <code className="font-mono text-foreground">finance.commission_plans</code>{' '}
        com clawback de 30d em refunds.
      </div>
    </div>
  );
}
